import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Modal from '../../components/ui/Modal';
import { Users, Plus, Copy, Ban, CheckCircle, KeyRound, Trash2, RefreshCw } from 'lucide-react';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import LiveBadge from '../../components/LiveBadge';

export default function ReferralStaffPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', password: '', player_bonus_sc: '', player_bonus_fp: '', staff_commission_sc: '', staff_commission_percent: '', min_first_deposit_sc: '' });
  const [editing, setEditing] = useState(null);

  const fetchStaff = async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/api/admin/referral-staff');
      setStaff(res.data.staff || []);
    } catch { if (!silent) toast.error('Failed to load referral staff'); }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { fetchStaff(); }, []);
  // Silent auto-sync so new RA accounts show up the moment another admin creates one.
  useAutoRefresh(() => fetchStaff({ silent: true }), 8000, !showCreate && !editing);

  const genPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let out = ''; for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setForm(f => ({ ...f, password: out }));
  };

  const handleCreate = async () => {
    if (!form.email.trim() || !form.password.trim()) { toast.error('Email and password are required'); return; }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setCreating(true);
    try {
      const bonus_rules = {};
      ['player_bonus_sc', 'player_bonus_fp', 'staff_commission_sc', 'staff_commission_percent', 'min_first_deposit_sc'].forEach(k => {
        if (form[k] !== '' && form[k] != null) bonus_rules[k] = Number(form[k]);
      });
      const res = await api.post('/api/admin/referral-staff', {
        email: form.email.trim(),
        full_name: form.full_name.trim() || undefined,
        password: form.password,
        bonus_rules: Object.keys(bonus_rules).length ? bonus_rules : undefined,
      });
      toast.success(`Referral staff created — code ${res.data?.referral_code}`);
      setShowCreate(false);
      setForm({ email: '', full_name: '', password: '', player_bonus_sc: '', player_bonus_fp: '', staff_commission_sc: '', staff_commission_percent: '', min_first_deposit_sc: '' });
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to create');
    } finally { setCreating(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const toggleSuspend = async (s) => {
    try {
      await api.put(`/api/admin/referral-staff/${s.id}`, { disabled: !s.is_suspended });
      toast.success(s.is_suspended ? 'Enabled' : 'Suspended');
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const bonus_rules = {};
      ['player_bonus_sc', 'player_bonus_fp', 'staff_commission_sc', 'staff_commission_percent', 'senior_override_percent', 'min_first_deposit_sc'].forEach(k => {
        if (editing[k] !== '' && editing[k] != null && editing[k] !== undefined) bonus_rules[k] = Number(editing[k]);
      });
      const payload = { bonus_rules };
      // Upline multi-tier — empty string explicitly clears the relation.
      if (editing.upline_staff_id !== undefined) {
        payload.upline_staff_id = editing.upline_staff_id || '';
      }
      if (editing.new_password) payload.new_password = editing.new_password;
      if (editing.full_name !== undefined) payload.full_name = editing.full_name;
      await api.put(`/api/admin/referral-staff/${editing.id}`, payload);
      toast.success('Updated');
      setEditing(null);
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete referral staff ${s.email}? Players they brought in will stay, but commissions stop.`)) return;
    try {
      await api.delete(`/api/admin/referral-staff/${s.id}`);
      toast.success('Deleted');
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="referral-staff-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-3"><Users className="w-5 h-5 text-[#D4AF37]" /> Referral Staff <LiveBadge /></h2>
          <p className="text-sm text-gray-400">Recruiting agents with <code className="text-[#D4AF37]">#RA…</code> codes. They can only see the players they refer.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#D4AF37] text-black font-semibold" data-testid="create-referral-staff-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Referral Staff
        </Button>
      </div>

      <div className="bg-[#131822] rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="referral-staff-table">
          <thead><tr className="border-b border-white/5 text-xs text-gray-500">
            <th className="text-left p-3">Staff</th>
            <th className="text-left p-3">Referral Code</th>
            <th className="text-right p-3">Player Bonus</th>
            <th className="text-right p-3">Staff Commission</th>
            <th className="text-right p-3">Min First Deposit</th>
            <th className="text-left p-3">Status</th>
            <th className="text-right p-3">Actions</th>
          </tr></thead>
          <tbody>
            {staff.map(s => (
              <tr key={s.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                <td className="p-3">
                  <div className="font-medium">{s.full_name || s.username}</div>
                  <div className="text-xs text-gray-500">{s.email}</div>
                </td>
                <td className="p-3">
                  <button onClick={() => copyCode(s.referral_code)} className="inline-flex items-center gap-1.5 text-[#D4AF37] font-mono text-sm hover:text-white" data-testid={`copy-code-${s.id}`}>
                    {s.referral_code} <Copy className="w-3 h-3" />
                  </button>
                </td>
                <td className="p-3 text-right text-green-400">{s.bonus_rules?.player_bonus_sc != null ? `$${s.bonus_rules.player_bonus_sc}` : <span className="text-gray-600 text-xs">default</span>}</td>
                <td className="p-3 text-right text-[#D4AF37]">
                  {s.bonus_rules?.staff_commission_percent != null
                    ? `${s.bonus_rules.staff_commission_percent}%`
                    : s.bonus_rules?.staff_commission_sc != null
                      ? `$${s.bonus_rules.staff_commission_sc}`
                      : <span className="text-gray-600 text-xs">default</span>}
                </td>
                <td className="p-3 text-right">{s.bonus_rules?.min_first_deposit_sc != null ? `$${s.bonus_rules.min_first_deposit_sc}` : <span className="text-gray-600 text-xs">none</span>}</td>
                <td className="p-3">
                  {s.is_suspended
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">Suspended</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">Active</span>}
                </td>
                <td className="p-3 text-right space-x-1">
                  <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => setEditing({
                    ...s,
                    player_bonus_sc: s.bonus_rules?.player_bonus_sc ?? '',
                    player_bonus_fp: s.bonus_rules?.player_bonus_fp ?? '',
                    staff_commission_sc: s.bonus_rules?.staff_commission_sc ?? '',
                    staff_commission_percent: s.bonus_rules?.staff_commission_percent ?? '',
                    senior_override_percent: s.bonus_rules?.senior_override_percent ?? '',
                    min_first_deposit_sc: s.bonus_rules?.min_first_deposit_sc ?? '',
                    upline_staff_id: s.upline_staff_id ?? '',
                    new_password: '',
                  })} data-testid={`edit-ra-${s.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => toggleSuspend(s)}>
                    {s.is_suspended ? <CheckCircle className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs" onClick={() => remove(s)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {staff.length === 0 && <div className="p-8 text-center text-gray-500">No referral staff yet. Click "Add Referral Staff" to create one.</div>}
      </div>

      {/* Create modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Referral Staff" data-testid="create-ra-modal">
        <div className="space-y-3">
          <Labelled label="Email">
            <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="agent@example.com" className="bg-[#0B0E14] border-white/10" data-testid="ra-email" />
          </Labelled>
          <Labelled label="Full Name (optional)">
            <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} placeholder="Jane Doe" className="bg-[#0B0E14] border-white/10" />
          </Labelled>
          <Labelled label="Password (min 6 chars)">
            <div className="flex gap-2">
              <Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Strong password" className="bg-[#0B0E14] border-white/10 font-mono" data-testid="ra-password" />
              <Button variant="outline" className="border-white/10 text-xs" onClick={genPassword}><RefreshCw className="w-3 h-3" /></Button>
            </div>
          </Labelled>
          <div className="grid grid-cols-2 gap-3">
            <Labelled label="Player Bonus (FP Credits)">
              <Input type="number" value={form.player_bonus_fp} onChange={e => setForm({ ...form, player_bonus_fp: e.target.value })} placeholder="e.g. 50" className="bg-[#0B0E14] border-white/10" data-testid="ra-player-bonus-fp" />
            </Labelled>
            <Labelled label="Staff Commission (%)">
              <Input type="number" min="0" max="100" step="0.1" value={form.staff_commission_percent} onChange={e => setForm({ ...form, staff_commission_percent: e.target.value })} placeholder="e.g. 10" className="bg-[#0B0E14] border-white/10" data-testid="ra-commission-percent" />
            </Labelled>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Labelled label="Player Bonus ($ legacy)">
              <Input type="number" value={form.player_bonus_sc} onChange={e => setForm({ ...form, player_bonus_sc: e.target.value })} placeholder="optional" className="bg-[#0B0E14] border-white/10" />
            </Labelled>
            <Labelled label="Staff Commission ($ legacy)">
              <Input type="number" value={form.staff_commission_sc} onChange={e => setForm({ ...form, staff_commission_sc: e.target.value })} placeholder="optional" className="bg-[#0B0E14] border-white/10" />
            </Labelled>
            <Labelled label="Min First Deposit ($)">
              <Input type="number" value={form.min_first_deposit_sc} onChange={e => setForm({ ...form, min_first_deposit_sc: e.target.value })} placeholder="none" className="bg-[#0B0E14] border-white/10" />
            </Labelled>
          </div>
          <p className="text-[10px] text-gray-500">FP Credits go to the new player; commission % is taken from their FIRST deposit amount and credited to your staff earnings.</p>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" className="border-white/10" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#D4AF37] text-black font-semibold" data-testid="submit-create-ra">
              {creating ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit ${editing?.email || ''}`}>
        {editing && (
          <div className="space-y-3">
            <Labelled label="Full Name"><Input value={editing.full_name || ''} onChange={e => setEditing({ ...editing, full_name: e.target.value })} className="bg-[#0B0E14] border-white/10" /></Labelled>
            <Labelled label="Reset Password (optional)"><Input value={editing.new_password || ''} onChange={e => setEditing({ ...editing, new_password: e.target.value })} placeholder="Leave blank to keep" className="bg-[#0B0E14] border-white/10 font-mono" /></Labelled>
            <div className="grid grid-cols-2 gap-3">
              <Labelled label="Player Bonus (FP)"><Input type="number" value={editing.player_bonus_fp} onChange={e => setEditing({ ...editing, player_bonus_fp: e.target.value })} className="bg-[#0B0E14] border-white/10" data-testid="ra-edit-player-bonus-fp" /></Labelled>
              <Labelled label="Staff Commission (%)"><Input type="number" min="0" max="100" step="0.1" value={editing.staff_commission_percent} onChange={e => setEditing({ ...editing, staff_commission_percent: e.target.value })} className="bg-[#0B0E14] border-white/10" data-testid="ra-edit-commission-percent" /></Labelled>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Labelled label="Senior-RA Override (%)"><Input type="number" min="0" max="100" step="0.1" value={editing.senior_override_percent || ''} onChange={e => setEditing({ ...editing, senior_override_percent: e.target.value })} placeholder="0" className="bg-[#0B0E14] border-white/10" data-testid="ra-edit-senior-override" /></Labelled>
              <Labelled label="Upline (Senior RA)">
                <select
                  value={editing.upline_staff_id || ''}
                  onChange={e => setEditing({ ...editing, upline_staff_id: e.target.value })}
                  className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                  data-testid="ra-edit-upline-select"
                >
                  <option value="">None (independent)</option>
                  {staff.filter(x => x.id !== editing.id).map(x => (
                    <option key={x.id} value={x.id}>{x.username} · {x.referral_code}</option>
                  ))}
                </select>
              </Labelled>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Labelled label="Player Bonus ($)"><Input type="number" value={editing.player_bonus_sc} onChange={e => setEditing({ ...editing, player_bonus_sc: e.target.value })} className="bg-[#0B0E14] border-white/10" /></Labelled>
              <Labelled label="Staff Commission ($)"><Input type="number" value={editing.staff_commission_sc} onChange={e => setEditing({ ...editing, staff_commission_sc: e.target.value })} className="bg-[#0B0E14] border-white/10" /></Labelled>
              <Labelled label="Min First Deposit ($)"><Input type="number" value={editing.min_first_deposit_sc} onChange={e => setEditing({ ...editing, min_first_deposit_sc: e.target.value })} className="bg-[#0B0E14] border-white/10" /></Labelled>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" className="border-white/10" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveEdit} className="bg-[#D4AF37] text-black font-semibold">Save</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Labelled({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold block mb-1">{label}</label>
      {children}
    </div>
  );
}
